import clsx from "clsx";
import type { FC, Ref, RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import "./GroupCardShared.scss";

import { toastLocalizedOperationCatcher } from "../../../../utils/alerts";
import { filterMap, reorderItemInCollection } from "../../../../utils/collections";

import { usePageGroupOperation } from "./use-page-group-operation";

import {
  serverClearPageGroupMemberships,
  serverDistributeUsersToGroups,
} from "api/api-server-actions";
import { GROUP_CONFIG_QUERY_PARAM } from "components/constants";
import { CopyPageGroups } from "components/materials/page/groups/CopyPageGroups";
import { PageBreakoutDuration } from "components/materials/page/groups/PageBreakoutDuration";
import { PageGroupEditCard } from "components/materials/page/groups/PageGroupEditCard";
import { StudentOptionsMenu } from "components/materials/page/groups/StudentOptionsMenu";
import {
  DraggablePageGroupUserType,
  DraggableSectionUserType,
  getUngroupedStudentUsers,
  moveSectionUserLocally,
} from "components/materials/page/groups/helpers";
import { GroupSizeConfig } from "components/materials/page/groups/menus/GroupSizeConfig";
import { InteractionModeConfig } from "components/materials/page/groups/menus/InteractionModeConfig";
import { ViewGroupMenu } from "components/materials/page/groups/menus/ViewGroupMenu";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { userActivityIcons } from "components/materials/section/helpers";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Card } from "mds/components/Card";
import { CloseButton } from "mds/components/CloseButton";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, LoadingDotsIcon, PersonSingleIcon } from "mds/icons";
import { store, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  SectionUserActivityStatus,
  pageSelectors,
  selectBreakoutSessionEndsAt,
  selectBreakoutSessionSyncedPageId,
  selectCurrentSectionId,
  selectCurrentSectionIsArchived,
  selectCurrentSectionIsLinked,
  selectCurrentSectionIsTemplate,
  selectIsAnyPageGroupFullForPage,
  selectPageGroupIdsByPage,
  selectPageGroupUserIdsForPage,
  selectPageGroupsMembershipHashByPageId,
  selectSectionUserIdsForPage,
  selectStudentsWithSectionActivityInCurrentSection,
  submissionSelectors,
} from "store/selectors";
import {
  selectAssessedSubmissionsByPageId,
  selectAssessmentsByPageId,
  selectSubmissionsByPageId,
} from "store/selectors/assessment";
import { sortByActiveStateThenFirstName } from "store/selectors/sorting";
import { viewStateActions } from "store/slices/view";
import {
  useFullListSelector,
  useHasItemsSelector,
  useListSelector,
  useObjectSelector,
} from "store/store-hooks";
import { useAv } from "utils/av";
import { useQueryParamsActions } from "utils/routing";
import { getUserFullName } from "utils/user-utils";

interface GroupConfigCardProps {
  pageId: string;
  triggerRef?: RefObject<HTMLButtonElement>;
  closeButtonRef?: Ref<HTMLButtonElement>;
}

export const GroupConfigCard: FC<GroupConfigCardProps> = ({
  pageId,
  triggerRef,
  closeButtonRef,
}) => {
  const dispatch = useAppDispatch();
  const { isInVirtualClass } = useAv();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const pageGroupIds = useListSelector((s) => selectPageGroupIdsByPage(s, pageId));
  const groupedSectionUserIds = useListSelector((s) => selectSectionUserIdsForPage(s, pageId));
  const hasPageGroupUsersOnPage = useHasItemsSelector((s) =>
    selectPageGroupUserIdsForPage(s, pageId),
  );

  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    [
      "assessments_published_at",
      "copied_groups_from_page_id",
      "has_copied_groups",
      "has_linked_submissions",
      "interaction_mode",
      "max_group_size",
      "submissions_last_collected_at",
    ],
  );
  const { deleteQueryParams } = useQueryParamsActions();
  const hideGroupConfig = useCallback(() => {
    deleteQueryParams([GROUP_CONFIG_QUERY_PARAM], { replace: true });
    dispatch(viewStateActions.setShowGroupConfig(false));
    if (triggerRef && triggerRef.current) {
      setTimeout(() => triggerRef.current.focus(), 0);
    }
  }, [deleteQueryParams, dispatch, triggerRef]);

  const submissionsExist = useHasItemsSelector((s) => selectSubmissionsByPageId(s, pageId));
  const hasAssessments = useHasItemsSelector((s) => selectAssessmentsByPageId(s, pageId));
  const assessedSubmissions = useListSelector(
    (s) => selectAssessedSubmissionsByPageId(s, pageId),
    ["page_group_id"],
  );
  const groupIdsWithAssessments = new Set(
    filterMap(
      assessedSubmissions,
      (submission) => Boolean(submission.page_group_id),
      (submission) => submission.page_group_id,
    ),
  );
  const isAssessing = useIsAssessing();
  // Student DnD list needs full user + activity fields for display/sorting.
  const students = useFullListSelector(selectStudentsWithSectionActivityInCurrentSection);
  const hasEnrolledStudents = students.length > 0;
  const studentUsers = useMemo(() => students.toSorted(sortByActiveStateThenFirstName), [students]);

  const [isDistributingStudents, setIsDistributingStudents] = useState(false);
  const hasSubmissions = Boolean(submissionsExist || page?.submissions_last_collected_at);
  const { isPending, runPageGroupOperation } = usePageGroupOperation(pageId);
  const isReadOnly = hasAssessments || isAssessing || isArchived || isPending;
  const isCopyGroupsInteractionMode = page?.interaction_mode === InteractionMode.COPY_GROUPS;
  const hasGroupSetup =
    !isCopyGroupsInteractionMode ||
    !!page?.has_copied_groups ||
    (isLinkedSection && pageGroupIds.length > 0);
  const syncedPageId = useAppSelector(selectBreakoutSessionSyncedPageId);
  const breakoutEndsAt = useAppSelector(selectBreakoutSessionEndsAt);
  const anySubmissionsExist = useHasItemsSelector((state) =>
    submissionSelectors.filterByPageId(state, pageId),
  );
  // Copy replaces memberships, so it retains its stricter submission restriction.
  // Method and size controls below continue to use the existing page-edit rules.
  const copyDisabledReason = page?.has_linked_submissions
    ? "linked_submissions"
    : isReadOnly
      ? "unavailable"
      : anySubmissionsExist || page?.submissions_last_collected_at
        ? "submissions"
        : syncedPageId === pageId && breakoutEndsAt
          ? "breakouts"
          : null;
  const areMembershipsReadOnly = isReadOnly || (isCopyGroupsInteractionMode && !hasGroupSetup);
  const showDeletePageGroupButton =
    !isAssessing && !isArchived && !isCopyGroupsInteractionMode && !isPending;

  // Local ungrouped order (e.g. removed student pinned to top) can disagree with
  // derivedUngrouped after membership changes. Stamp overrides with a membership
  // fingerprint and clear them when it no longer matches.
  const [localUngroupedOverride, setLocalUngroupedOverride] = useState<{
    membershipHash: string;
    users: DraggableSectionUserType[];
  } | null>(null);
  const derivedUngrouped = useMemo(
    () => getUngroupedStudentUsers(groupedSectionUserIds, studentUsers),
    [groupedSectionUserIds, studentUsers],
  );
  const membershipHash = useAppSelector((state) =>
    selectPageGroupsMembershipHashByPageId(state, pageId),
  );
  // Clear (don't just ignore) when membership changes — otherwise a later return to the
  // previous hash (undo / re-add) would revive a stale ungrouped snapshot.
  if (localUngroupedOverride !== null && localUngroupedOverride.membershipHash !== membershipHash) {
    setLocalUngroupedOverride(null);
  }
  const localUngrouped =
    localUngroupedOverride?.membershipHash === membershipHash ? localUngroupedOverride.users : null;
  const ungroupedStudentUsers = localUngrouped ?? derivedUngrouped;
  // moveRemovedStudentToTop is passed down through PageGroupEditCard into StudentOptionsMenu where it is
  // used to handle clicks on the "Remove from this Group" button. StudentOptionsMenu performs the
  // DELETE request on the PageGroupUser which causes it to disappear from the PageGroupEditCard and appear
  // in the ungrouped students list, and then moveRemovedStudentToTop moves it to the top of that list.
  const moveRemovedStudentToTop = (studentSectionUserId: string) => {
    const removed = studentUsers.find((u) => u.section_user_id === studentSectionUserId);
    if (!removed) return;
    const item: DraggableSectionUserType = { ...removed, order: -1 };
    const collection: DraggableSectionUserType[] = ungroupedStudentUsers.map((u) => ({ ...u }));
    const newCollection = reorderItemInCollection(item, collection, 0);
    // Destroy already finished, but this callback may still close over the pre-destroy
    // membershipHash. Tag with the post-remove hash so the render-time clear doesn't wipe
    // the override when membership updates.
    setLocalUngroupedOverride({
      membershipHash: selectPageGroupsMembershipHashByPageId(
        store.getState(),
        pageId,
        studentSectionUserId,
      ),
      users: newCollection,
    });
  };

  // onDropSectionUser handles drop events in both GroupConfigCard and PageGroupEditCard. It consults
  // the value of page_group_id set on the dragged item in moveSectionUserLocally and the value of
  // sourceContainerId from the drop event and performs the appropriate API request. The value of
  // sourceContainerId will be the special value "ungrouped" when the item is being dragged out
  // of the ungrouped students list, or a PageGroup id when being dragged out of a PageGroupEditCard.
  const onDropSectionUser = (
    droppedItem: DraggableSectionUserType | DraggablePageGroupUserType,
    _toIndex: number,
    sourceContainerId: string,
  ) => {
    if (
      store.getState().view.pendingPageGroupActions[pageId] ||
      areMembershipsReadOnly ||
      !sectionId ||
      droppedItem.page_group_id === sourceContainerId
    ) {
      // No-op if the item is being dropped back into the same container it came from.
      return;
    }

    if (sourceContainerId === "ungrouped") {
      storeApi.page_group_users.create({
        section_id: sectionId,
        page_group_id: droppedItem.page_group_id,
        section_user_id: droppedItem.section_user_id,
      });
    } else if (droppedItem.page_group_id === "ungrouped") {
      // A DraggablePageGroupUserType object is being dragged from a PageGroupEditCard and dropped
      // into the ungrouped students list, and the corresponding PageGroupUser needs to be deleted.
      storeApi.page_group_users.destroy(droppedItem.id);
    } else {
      // A DraggablePageGroupUserType object is being dragged from one PageGroupEditCard and dropped
      // into another, and the corresponding PageGroupUser needs to be updated.
      storeApi.page_group_users.partial_update({
        id: droppedItem.id,
        page_group_id: droppedItem.page_group_id,
      });
    }
  };

  // If all section_users are grouped but there is a group that exceeds the max group size limit,
  // the Distribute Students button should be re-enabled.
  const isAssignedGroupsInteractionMode =
    page?.interaction_mode === InteractionMode.ASSIGN_STUDENTS;
  const isStudentSelfAssignInteractionMode = page?.interaction_mode === InteractionMode.SELF_ASSIGN;
  const areAllStudentsGrouped = ungroupedStudentUsers.length === 0;
  const isMaxGroupSizeSet = page?.max_group_size !== null;
  // Consider a group at or above the size limit as a candidate for redistribution.
  const isAnyGroupAtOrOverSizeLimit = useAppSelector((state) =>
    selectIsAnyPageGroupFullForPage(state, pageId),
  );
  const noStudentsLeftToDistribute =
    areAllStudentsGrouped && (!isMaxGroupSizeSet || !isAnyGroupAtOrOverSizeLimit);
  const isDistributeDisabled =
    !isAssignedGroupsInteractionMode ||
    pageGroupIds.length === 0 ||
    noStudentsLeftToDistribute ||
    isDistributingStudents ||
    areMembershipsReadOnly;
  const distributeStudentsButtonTooltip = hasAssessments
    ? t("tooltip.group_config_has_assessments")
    : pageGroupIds.length === 0
      ? t("tooltip.distribute.no_groups")
      : noStudentsLeftToDistribute
        ? t("tooltip.distribute.no_students")
        : t("tooltip.distribute.default");

  const onDistributeStudents = () => {
    if (isDistributeDisabled) return;
    setIsDistributingStudents(true);
    const sectionUserIds = isInVirtualClass
      ? filterMap(
          studentUsers,
          (user) => user.sectionActivityStatus !== SectionUserActivityStatus.OFFLINE,
          (user) => user.section_user_id,
        )
      : null;

    serverDistributeUsersToGroups(pageId, sectionUserIds)
      .catch(() => toastLocalizedOperationCatcher("failed_to_distribute_users_to_groups"))
      .finally(() => setIsDistributingStudents(false));
  };

  const isInteractionModeReadOnly =
    !isInTemplateSection && (isReadOnly || hasSubmissions || isLinkedSection);
  const settingsTooltip = isLinkedSection
    ? t("group_configuration_card.inherited_settings_hint")
    : hasSubmissions
      ? t("tooltip.group_config_has_submissions")
      : undefined;
  const sizeTooltip = isCopyGroupsInteractionMode
    ? t("group_configuration_card.copied_size_hint")
    : t("tooltip.group_size");
  const isResetGroupsDisabled = !hasPageGroupUsersOnPage || isReadOnly;
  const resetTooltip = hasAssessments
    ? t("tooltip.group_config_has_assessments")
    : t("tooltip.reset_groups");
  const onResetGroups = () => {
    if (isResetGroupsDisabled) return;
    runPageGroupOperation(() => serverClearPageGroupMemberships(pageId)).catch(
      toastLocalizedOperationCatcher("failed_to_reset_groups"),
    );
  };

  if (!page || !sectionId) {
    return null;
  }

  const interactionModeControl = (
    <InteractionModeConfig
      isDisabled={isPending}
      isReadOnly={isInteractionModeReadOnly}
      pageId={pageId}
      title={settingsTooltip ?? t("group_configuration_card.tooltip.group_selection_settings")}
    />
  );

  const groupSizeControl = (
    <GroupSizeConfig
      isDisabled={isPending}
      isReadOnly={
        isCopyGroupsInteractionMode || (!isInTemplateSection && (isReadOnly || isLinkedSection))
      }
      pageId={pageId}
      title={sizeTooltip}
    />
  );

  if (isInTemplateSection) {
    return (
      <div className="group-card flex w-full flex-col gap-4 rounded-[20px] border border-black-tint-90 bg-black-tint-97 p-6 @container/group-config supports-squircle:rounded-[40px] supports-squircle:corner-squircle min-[480px]:p-6">
        <div className="flex w-full items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="body-m text-black-tint-20">{t("group_configuration_card.title")}</h3>
            <p className="body-xs mt-1 text-black-tint-40">
              {t(
                isCopyGroupsInteractionMode
                  ? "group_configuration_card.copy_template_hint"
                  : "group_configuration_card.template_hint",
              )}
            </p>
          </div>

          <CloseButton
            aria-label={t("group_configuration_card.tooltip.close_group_settings")}
            ref={closeButtonRef}
            onClick={hideGroupConfig}
          />
        </div>

        <div className="flex w-full flex-shrink-0 flex-wrap gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1 [&_.mds-menu]:w-full [&_.mds-tooltip-source]:flex [&_.mds-tooltip-source]:w-full [&_.mds-tooltip]:w-full">
            <span className="body-xs text-black-tint-40">
              {t("group_configuration_card.group_formation")}
            </span>
            <InteractionModeConfig
              isDisabled={isPending}
              isReadOnly={isInteractionModeReadOnly}
              pageId={pageId}
              title={
                settingsTooltip ?? t("group_configuration_card.tooltip.group_selection_settings")
              }
              fullWidth
            />
          </div>

          {!isCopyGroupsInteractionMode && (
            <div className="flex min-w-0 flex-1 flex-col gap-1 [&_.mds-menu]:w-full [&_.mds-tooltip-source]:flex [&_.mds-tooltip-source]:w-full [&_.mds-tooltip]:w-full">
              <span className="body-xs text-black-tint-40">
                {t("group_configuration_card.group_size_label")}
              </span>
              <GroupSizeConfig
                isDisabled={isPending}
                isReadOnly={
                  isCopyGroupsInteractionMode ||
                  (!isInTemplateSection && (isReadOnly || isLinkedSection))
                }
                pageId={pageId}
                title={sizeTooltip}
                fullWidth
              />
            </div>
          )}
        </div>

        {isCopyGroupsInteractionMode && (
          <CopyPageGroups disabled={!!copyDisabledReason} pageId={pageId} isTemplate />
        )}
        <PageBreakoutDuration hasAssessments={hasAssessments} key={pageId} pageId={pageId} />

        <div className="flex w-full justify-end">
          <SquareButton
            eventName="Group settings - Done"
            eventProps={{ pageGroupCount: pageGroupIds.length }}
            kind="accent"
            size="s"
            onClick={hideGroupConfig}
          >
            {t("common.done")}
          </SquareButton>
        </div>
      </div>
    );
  }

  // Max height calculation subtracts UI elements from viewport height:
  // - --navbar-height, --footer-height: main navigation elements
  // - --participant-bar-height: virtual class participant bar (0px for in-person)
  // - 40px: margins/padding around component
  // - 148px: height of the page title + tags
  return (
    <div
      className={clsx(
        "group-card flex w-full flex-col gap-4 overflow-y-auto rounded-[20px] border border-black-tint-90 bg-black-tint-97 p-6 @container/group-config [max-height:calc(100vh-var(--navbar-height)-var(--footer-height)-var(--participant-bar-height)-40px-148px)] supports-squircle:rounded-[40px] supports-squircle:corner-squircle",
        hasEnrolledStudents && hasGroupSetup && "min-h-[330px]",
      )}
    >
      <div className="grid w-full flex-shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3 @[600px]/group-config:grid-cols-[minmax(0,1fr)_auto_auto]">
        <div className="min-w-0">
          <h3 className="body-m text-black-tint-20">{t("group_configuration_card.title")}</h3>
          <p className="body-s mt-1 text-black-tint-40">
            {t("group_configuration_card.description")}
          </p>
        </div>

        <div className="col-span-2 row-start-2 flex flex-wrap items-center gap-2 @[600px]/group-config:col-span-1 @[600px]/group-config:col-start-2 @[600px]/group-config:row-start-1">
          {isInVirtualClass && pageGroupIds.length > 0 && <ViewGroupMenu pageId={pageId} />}
          {interactionModeControl}
        </div>

        <CloseButton
          aria-label={t("group_configuration_card.tooltip.close_group_settings")}
          className="col-start-2 row-start-1 @[600px]/group-config:col-start-3"
          ref={closeButtonRef}
          onClick={hideGroupConfig}
        />
      </div>

      {isLinkedSection && (
        <Banner className="w-full" kind="info">
          {t("group_configuration_card.inherited_settings_hint")}
        </Banner>
      )}

      {isCopyGroupsInteractionMode && (
        <CopyPageGroups
          disabled={!!copyDisabledReason}
          isInherited={isLinkedSection}
          key={`copy-${pageId}`}
          pageId={pageId}
        />
      )}
      <PageBreakoutDuration hasAssessments={hasAssessments} key={pageId} pageId={pageId} />

      {isCopyGroupsInteractionMode && copyDisabledReason && (
        <p className="body-s text-black-tint-40" role="status">
          {t(`group_configuration_card.copy_disabled.${copyDisabledReason}`, {
            defaultValue: t("group_configuration_card.copy_disabled.unavailable"),
          })}
        </p>
      )}

      {isAssessing && !page.assessments_published_at && (
        <div className="w-full">
          <Banner kind="info">{t("group_configuration_card.anonymity_alert")}</Banner>
        </div>
      )}

      {/* Reserve space for the heading, two student rows, and the distribution action. */}
      {hasGroupSetup &&
        (hasEnrolledStudents ? (
          <div
            className={clsx(
              "@max-[460px]/group-config:min-h-64 flex min-h-[12rem] w-full min-w-0 max-w-full flex-1 gap-3 overflow-x-hidden overflow-y-hidden",
              isCopyGroupsInteractionMode &&
                !page?.copied_groups_from_page_id &&
                !page?.has_copied_groups &&
                "opacity-50",
            )}
          >
            <div className="flex min-h-[8rem] min-w-0 flex-[1_0_0] flex-col gap-3">
              <DragAndDrop
                className="flex min-h-[8rem] w-full flex-1"
                dragItemKind="section_user"
                dropContainerId="ungrouped"
                droppable={!areMembershipsReadOnly}
                onDraggedItemHover={moveSectionUserLocally}
              >
                <Card
                  className="flex min-h-[8rem] flex-1 flex-col overflow-hidden p-3"
                  data-testid="students-card"
                  kind="outlined"
                >
                  <div className="@max-[460px]/group-config:flex-wrap @max-[460px]/group-config:gap-y-1 mb-1 flex min-h-8 w-full shrink-0 items-center justify-between">
                    <div className="body-m @max-[460px]/group-config:max-w-full @max-[460px]/group-config:shrink-0 @max-[460px]/group-config:whitespace-normal @max-[460px]/group-config:[overflow-wrap:anywhere] ml-2 truncate">
                      {t("group_configuration_card.students")}
                    </div>
                    <TooltipTrigger
                      content={t("group_configuration_card.tooltip.ungrouped_students", {
                        count: ungroupedStudentUsers.length,
                        total: studentUsers.length,
                      })}
                    >
                      <div className="body-xs flex items-center justify-center gap-1 px-2 text-black-tint-40">
                        <PersonSingleIcon />
                        {ungroupedStudentUsers.length}/{studentUsers.length}
                      </div>
                    </TooltipTrigger>
                  </div>

                  {/* Two ListItem rows: 36px height + 4px vertical margins each. */}
                  <div className="min-h-[5rem] flex-1 overflow-y-auto">
                    {ungroupedStudentUsers.map((section_user) => (
                      <DragAndDrop
                        className="w-full"
                        draggable={!areMembershipsReadOnly}
                        dragItemKind="section_user"
                        dropContainerId="ungrouped"
                        item={section_user}
                        key={section_user.id}
                        onDropItem={onDropSectionUser}
                      >
                        <ListItem
                          className="@max-[460px]/group-config:h-auto @max-[460px]/group-config:min-h-9 @max-[460px]/group-config:px-0 @max-[460px]/group-config:text-[14px] flex w-full items-center justify-between"
                          data-section-user-id={section_user.section_user_id}
                          data-testid={`row-${section_user.section_user_id}`}
                          grabbable={!areMembershipsReadOnly}
                          key={section_user.id}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            {userActivityIcons[section_user.sectionActivityStatus]}
                            <div className="body-s @max-[460px]/group-config:min-w-0 @max-[460px]/group-config:whitespace-normal @max-[460px]/group-config:[overflow-wrap:anywhere] truncate">
                              {getUserFullName(section_user)}
                            </div>
                          </div>

                          <StudentOptionsMenu
                            currentGroupId={undefined}
                            isInGroup={false}
                            isReadOnly={areMembershipsReadOnly}
                            pageId={pageId}
                            studentSectionUserId={section_user.section_user_id}
                          />
                        </ListItem>
                      </DragAndDrop>
                    ))}
                  </div>

                  {isAssignedGroupsInteractionMode && (
                    <div className="mt-2 shrink-0">
                      <Tooltip
                        content={isDistributeDisabled ? distributeStudentsButtonTooltip : ""}
                        wrapperClassName="flex w-full [&>.mds-tooltip-source]:w-full"
                      >
                        <SquareButton
                          className="@max-[460px]/group-config:h-auto @max-[460px]/group-config:min-h-[34px] @max-[460px]/group-config:whitespace-normal @max-[460px]/group-config:py-1 w-full"
                          disabled={isDistributeDisabled}
                          eventName="Group settings - Distribute students"
                          kind="accent"
                          size="s"
                          title={!isDistributeDisabled ? distributeStudentsButtonTooltip : ""}
                          onClick={onDistributeStudents}
                        >
                          {isInVirtualClass
                            ? t("group_configuration_card.distribute_online_students")
                            : t("group_configuration_card.distribute_students")}
                        </SquareButton>
                      </Tooltip>
                    </div>
                  )}
                </Card>
              </DragAndDrop>
            </div>

            {isDistributingStudents ? (
              <div className="flex w-full flex-[1_0_0] justify-center">
                <div className="pt-7">
                  <LoadingDotsIcon />
                </div>
              </div>
            ) : (
              <div className="flex min-h-[8rem] min-w-0 flex-[1_0_0] flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <div className="flex flex-col gap-3">
                    {pageGroupIds.map((pageGroupId, index) => (
                      <PageGroupEditCard
                        hasAssessedSubmissions={groupIdsWithAssessments.has(pageGroupId)}
                        isReadOnly={areMembershipsReadOnly}
                        key={pageGroupId}
                        moveSectionUserLocally={moveSectionUserLocally}
                        number={index + 1}
                        pageGroupId={pageGroupId}
                        pageId={pageId}
                        showDeleteButton={showDeletePageGroupButton}
                        showEmptyGroupTip={hasEnrolledStudents}
                        totalPageGroups={pageGroupIds.length}
                        onDropSectionUser={onDropSectionUser}
                        onRemove={moveRemovedStudentToTop}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="w-full py-4 text-center text-black-tint-40">
            {t("group_configuration_card.no_students_enrolled")}
          </p>
        ))}

      {hasGroupSetup && (
        <div className="flex w-full flex-shrink-0 flex-wrap items-center justify-between gap-y-2">
          {hasEnrolledStudents && (
            <Tooltip content={resetTooltip}>
              {(tooltipAnchorProps) => (
                <SquareButton
                  {...tooltipAnchorProps}
                  aria-disabled={isResetGroupsDisabled}
                  eventName="Group settings - Reset Groups"
                  kind="ghost"
                  size="s"
                  onClick={onResetGroups}
                >
                  {t("group_configuration_card.reset")}
                </SquareButton>
              )}
            </Tooltip>
          )}

          <div className={clsx("flex flex-wrap gap-1", hasEnrolledStudents ? "flex-1" : "ml-auto")}>
            {(isAssignedGroupsInteractionMode ||
              isStudentSelfAssignInteractionMode ||
              isCopyGroupsInteractionMode) &&
              groupSizeControl}

            {hasEnrolledStudents && (
              <SquareButton
                disabled={areMembershipsReadOnly}
                eventName="Group settings - Add new group"
                kind="ghost"
                size="s"
                onClick={() =>
                  storeApi.page_groups.create({ page_id: pageId, section_id: sectionId })
                }
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.page_group")} />
              </SquareButton>
            )}

            <SquareButton
              className="ml-auto"
              eventName="Group settings - Done"
              eventProps={{ pageGroupCount: pageGroupIds.length }}
              kind="tertiary"
              size="s"
              onClick={hideGroupConfig}
            >
              {t("common.done")}
            </SquareButton>
          </div>
        </div>
      )}
    </div>
  );
};
